import { Link } from "react-router-dom";
import { Package, Truck, CheckCircle2, AlertTriangle, Clock, Receipt, ArrowRight } from "lucide-react";
import api, { zar } from "@/lib/api";
import { StatCard, PageHeader, useFetch, Spinner } from "@/components/portal/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

function fmt(t) { return t ? new Date(t).toLocaleDateString("en-ZA", { dateStyle: "medium" }) : "—"; }

export default function Dashboard() {
  const { data, loading } = useFetch(() => api.get("/portal/dashboard").then((r) => r.data));
  if (loading) return <Spinner />;
  const s = data.stats;

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Your shipments and account at a glance">
        <Link to="/quote"><Button className="bg-primary hover:bg-red-700" data-testid="dash-quote-btn">Request a Quote</Button></Link>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-active" label="Active Shipments" value={s.active} icon={Package} />
        <StatCard testid="stat-transit" label="In Transit" value={s.in_transit} icon={Truck} accent="text-blue-600" />
        <StatCard testid="stat-delivered" label="Delivered" value={s.delivered} icon={CheckCircle2} accent="text-emerald-600" />
        <StatCard testid="stat-delayed" label="Delayed / At Risk" value={s.delayed} icon={AlertTriangle} accent="text-orange-600" />
        <StatCard testid="stat-pending" label="Pending Pickup" value={s.pending_pickup} icon={Clock} />
        <StatCard testid="stat-outstanding" label="Outstanding Invoices" value={s.outstanding_invoices} icon={Receipt} accent="text-red-600" sub={zar(s.outstanding_amount)} />
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between p-5 border-b border-border">
            <h2 className="font-heading font-bold">Recent shipments</h2>
            <Link to="/app/shipments" className="text-sm text-primary font-semibold flex items-center gap-1">View all <ArrowRight size={14} /></Link>
          </div>
          <div className="divide-y divide-border">
            {data.recent_shipments.map((sh) => (
              <Link key={sh.id} to={`/app/shipments/${sh.id}`} data-testid={`dash-ship-${sh.tracking_number}`}
                className="flex items-center justify-between p-4 hover:bg-muted/40 transition-colors">
                <div>
                  <p className="font-mono text-sm font-semibold">{sh.tracking_number}</p>
                  <p className="text-sm text-muted-foreground">{sh.origin} → {sh.destination}</p>
                </div>
                <div className="text-right flex items-center gap-4">
                  <span className="text-xs text-muted-foreground hidden sm:block">{fmt(sh.eta)}</span>
                  <StatusBadge status={sh.status} />
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card">
          <div className="p-5 border-b border-border"><h2 className="font-heading font-bold">Upcoming deliveries</h2></div>
          <div className="divide-y divide-border">
            {data.upcoming_deliveries.length === 0 && <p className="p-5 text-sm text-muted-foreground">No upcoming deliveries.</p>}
            {data.upcoming_deliveries.map((sh) => (
              <div key={sh.id} className="p-4">
                <p className="font-mono text-sm font-semibold">{sh.tracking_number}</p>
                <p className="text-sm text-muted-foreground">{sh.destination} · {fmt(sh.eta)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
